<template>
    <div class="delivery-lookup">
        <div class="message-box">
            <div class="message-box__search-box">
                <div class="message-box__search-box__input-group input-group">
                    <input
                        ref="autocompleteInput"
                        type="text"
                        class="form-control"
                        placeholder="Enter your address"
                        v-model="address"
                    />
                    <button
                        type="button"
                        class="btn btn-outline-secondary clear-btn"
                        @click="clearAddress()"
                        v-if="address"
                        title="Clear address"
                    >
                        <svg focusable="false" role="img">
                            <use xlink:href="/build/assets/icons.svg#close" href="/build/assets/icons.svg#close" />
                        </svg>
                    </button>
                </div>
                <div v-if="error" class="invalid-feedback">
                    {{ error }}
                </div>
            </div>
        </div>

        <div v-if="deliveryStatus" class="message-box mt-2">
            <div class="message-box__label">
                <h4>Delivery Status</h4>
                {{ deliveryStatus.message }}
            </div>
            <div class="message-box__value">
                <div
                    class="pill"
                    :class="deliveryStatus.available ? 'pill-primary' : 'pill-danger'"
                >
                    {{ deliveryStatus.available ? 'Available' : 'Not available'  }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import axios from 'axios'

// Storage keys for session address and lookup cache
const SESS_KEY = 'ec_last_used_addr'
const LOOKUP_KEY = 'ec_addr_lookup'

const props = defineProps({
    googleMapsApiKey: {type: String, required: true},
    productHandle: {type: String, default: ''}
})

const address = ref('')
const addressDetails = ref({address: '', suburb: '', state: '', postcode: '', lat: null, lng: null})
const error = ref('')
const deliveryStatus = ref(null)
const autocompleteInput = ref(null)
const detectedProductHandle = ref('')
let placeAutocompleteElement = null

// Purge any lookup entries not matching the current address
const clearStaleLookups = () => {
    try {
        const map = JSON.parse(localStorage.getItem(LOOKUP_KEY) || '{}')
        Object.keys(map).forEach(key => {
            const entry = map[key]
            if (
                entry.address !== addressDetails.value.address ||
                entry.suburb !== addressDetails.value.suburb ||
                entry.state !== addressDetails.value.state ||
                entry.postcode !== addressDetails.value.postcode
            ) {
                delete map[key]
            }
        })
        localStorage.setItem(LOOKUP_KEY, JSON.stringify(map))
    } catch (e) {
        console.error('Error clearing stale lookups:', e)
    }
}

// Store the current address details for checkout and caching
const storeAddressDetails = () => {
    const data = {
        address: addressDetails.value.address,
        suburb: addressDetails.value.suburb,
        state: addressDetails.value.state,
        postcode: addressDetails.value.postcode,
        lat: addressDetails.value.lat,
        lng: addressDetails.value.lng,
        dispatch: 'delivery'
    }
    // session storage for checkout
    sessionStorage.setItem('ec_checkout_addr', JSON.stringify(data))
    const rec = {...data, timestamp: Date.now()}
    sessionStorage.setItem(SESS_KEY, JSON.stringify(rec))
    // local storage fallback
    localStorage.setItem(SESS_KEY, JSON.stringify(rec))
}

// Load stored address (session → local)
const loadStoredAddress = () => {
    let stored = null
    stored = JSON.parse(sessionStorage.getItem(SESS_KEY) || 'null')
    if (!stored) stored = JSON.parse(localStorage.getItem(SESS_KEY) || 'null')

    if (stored) {
        addressDetails.value = {
            address: stored.address,
            suburb: stored.suburb,
            state: stored.state,
            postcode: stored.postcode,
            lat: stored.lat ?? null,
            lng: stored.lng ?? null
        }
        address.value = `${stored.address}, ${stored.suburb}, ${stored.state} ${stored.postcode}`
        if (autocompleteInput.value) {
            autocompleteInput.value.value = address.value
            autocompleteInput.value.classList.add('pac-target-input')
        }
        storeAddressDetails()
        return true
    }
    return false
}

// Load cached product lookup; clear and return false if forced or missing
const loadStoredLookupResult = (forceNew = false) => {
    if (forceNew || !detectedProductHandle.value) return false
    try {
        const map = JSON.parse(localStorage.getItem(LOOKUP_KEY) || '{}')
        const stored = map[detectedProductHandle.value]
        if (!stored) return false
        const fresh = (Date.now() - stored.timestamp) < 24 * 3600 * 1000
        if (fresh) {
            deliveryStatus.value = {available: stored.available, message: stored.message}
            return true
        }
    } catch (e) {
        console.error('Error loading lookup:', e)
    }
    return false
}

// Store latest lookup per product
const storeLookupResult = (res) => {
    if (!detectedProductHandle.value) return
    try {
        const map = JSON.parse(localStorage.getItem(LOOKUP_KEY) || '{}')
        map[detectedProductHandle.value] = {
            address: addressDetails.value.address,
            suburb: addressDetails.value.suburb,
            state: addressDetails.value.state,
            postcode: addressDetails.value.postcode,
            lat: addressDetails.value.lat,
            lng: addressDetails.value.lng,
            available: res.available,
            message: res.message,
            timestamp: Date.now()
        }
        localStorage.setItem(LOOKUP_KEY, JSON.stringify(map))
    } catch (e) {
        console.error('Error storing lookup:', e)
    }
}

// Perform delivery check, using cache when possible
const checkDeliveryAvailability = async (forceNew = false) => {

    error.value = ''
    if (loadStoredLookupResult(forceNew)) return
    try {
        // Use the unified endpoint
        const payload = {
            ...addressDetails.value,
            handle: detectedProductHandle.value
        }
        const resp = await axios.post('/api/check-delivery', payload)
        const d = resp.data

        if (typeof d.available === 'boolean' && typeof d.message === 'string') {
            // Direct response for a single product
            deliveryStatus.value = {
                available: d.available,
                message: d.message
            }
            storeLookupResult(deliveryStatus.value)
        } else if (d.results && d.results[detectedProductHandle.value]) {
            // Response contains results for multiple products
            const productResult = d.results[detectedProductHandle.value]
            deliveryStatus.value = {
                available: productResult.available,
                message: productResult.message
            }
            storeLookupResult(deliveryStatus.value)
        } else {
            throw new Error('Unexpected API format')
        }
    } catch (err) {
        console.error('Delivery check failed:', err)

        sessionStorage.removeItem('ec_checkout_addr')
        sessionStorage.removeItem(SESS_KEY)
        localStorage.removeItem(SESS_KEY)

        error.value = 'Failed to check delivery availability. Please try again.'
        const fb = {available: false, message: 'Delivery not available'}
        deliveryStatus.value = fb
        storeLookupResult(fb)
    }
}

// Initialize Google Places Autocomplete
const initPlaceAutocomplete = () => {
    if (!window.google || !autocompleteInput.value) return
    placeAutocompleteElement = new window.google.maps.places.Autocomplete(
        autocompleteInput.value,
        {componentRestrictions: {country: 'au'}, types: ['address']}
    )
    placeAutocompleteElement.addListener('place_changed', () => {
        handlePlaceSelect({place: placeAutocompleteElement.getPlace()})
    })
    if (address.value) checkDeliveryAvailability(false)
}

// Check if Google Maps script is loaded and initialize autocomplete
const checkGoogleMapsLoaded = () => {
    if (window.google?.maps?.places?.Autocomplete) {
        initPlaceAutocomplete();
        return true;
    }
    return false;
}

// Wait for Google Maps to load if it's not already loaded
const waitForGoogleMaps = () => {
    if (checkGoogleMapsLoaded()) return;

    // If not loaded yet, check again in 100ms
    const interval = setInterval(() => {
        if (checkGoogleMapsLoaded()) {
            clearInterval(interval);
        }
    }, 100);

    // Set a timeout to stop checking after 10 seconds
    setTimeout(() => {
        clearInterval(interval);
    }, 10000);
}

// Handle user selection from Google Places
const handlePlaceSelect = ({place}) => {
    addressDetails.value = {address: '', suburb: '', state: '', postcode: '', lat: null, lng: null}

    if(place) {

        if (!place.address_components) {
            address.value = place.formatted_address || ''
            Object.assign(addressDetails.value, {address, suburb: 'Unknown', state: 'WA', postcode: '0000'})
        } else {
            for (const comp of place.address_components) {
                const t = comp.types[0]
                if (t === 'street_number') addressDetails.value.address = comp.long_name
                if (t === 'route') addressDetails.value.address += (addressDetails.value.address ? ' ' : '') + comp.long_name
                if (t === 'locality') addressDetails.value.suburb = comp.long_name
                if (t === 'administrative_area_level_1') addressDetails.value.state = comp.short_name
                if (t === 'postal_code') addressDetails.value.postcode = comp.long_name
            }
            address.value = place.formatted_address
            if (place.geometry?.location) {
                addressDetails.value.lat = place.geometry.location.lat()
                addressDetails.value.lng = place.geometry.location.lng()
            }
        }

    }
    // purge any old lookups before storing new address
    clearStaleLookups()
    storeAddressDetails()
    checkDeliveryAvailability(false)
}

// Helpers for product handle and new-product detection
const extractProductHandleFromUrl = () => {
    const parts = window.location.pathname.split('/')
    const idx = parts.findIndex(p => p === 'products' || p === 'product')
    return idx !== -1 && parts.length > idx + 1 ? parts[idx + 1] : ''
}
const isNewProductVisit = (h) => {
    const last = localStorage.getItem('ec_last_visited')
    if (!last || last !== h) {
        localStorage.setItem('ec_last_visited', h);
        return true
    }
    return false
}

// Clear everything including the lookup cache for this product
const clearAddress = () => {
    address.value = ''
    addressDetails.value = {address: '', suburb: '', state: '', postcode: '', lat: null, lng: null}
    deliveryStatus.value = null
    error.value = ''
    initPlaceAutocomplete()
    sessionStorage.removeItem('ec_checkout_addr')
    sessionStorage.removeItem(SESS_KEY)
    localStorage.removeItem(SESS_KEY)
    try {
        const map = JSON.parse(localStorage.getItem(LOOKUP_KEY) || '{}')
        delete map[detectedProductHandle.value]
        localStorage.setItem(LOOKUP_KEY, JSON.stringify(map))
    } catch {
    }
}

// Listen for address-updated event
const handleAddressUpdated = () => {
    loadStoredAddress()
    checkDeliveryAvailability(false)
    console.log('address done');
}

// Lifecycle
onMounted(() => {
    detectedProductHandle.value = props.productHandle || extractProductHandleFromUrl()
    waitForGoogleMaps()
    const hadAddr = loadStoredAddress()
    const isNew = isNewProductVisit(detectedProductHandle.value)
    if (hadAddr) {
        const hadLookup = loadStoredLookupResult(isNew)
        if (!hadLookup) checkDeliveryAvailability(isNew)
    }

    // Add event listener for address-updated event
    window.addEventListener('address-updated', handleAddressUpdated)

    // Remove event listener when component is unmounted
    return () => {
        window.removeEventListener('address-updated', handleAddressUpdated)
    }
})

defineExpose({addressDetails, clearAddress, detectedProductHandle, deliveryStatus})
</script>
